import React, { useEffect, useRef, useContext } from "react";
import { BryntumGrid } from "@bryntum/schedulerpro-react";
import BryntumContext from "../context/bryntumContext.jsx";
import { EDITOR_KEYS } from "../../other/constants/EditorKeys.jsx";
import { callOnLoadOnColumns, canOpenEditor, debounce } from "../../other/utils/otherUtils.jsx";
import { handleIncentiveGridContextMenuOptions } from "../../other/utils/incentiveUtils.jsx";
import {
  getOriginalRecord,
  showConfirmationDialog,
  swivuCheckGridGrants,
} from "../../other/utils/utils.jsx";
import AuthContext from "../context/authContext.jsx";
import { INCENTIVE_POINTS_GRID_CONFIG } from "../../other/constants/IncentivePointsGridConfig.jsx";
import AlertNotificationBanner from "../components/alertNotificationBanner/AlertNotificationBanner.jsx";
import useRefsInit from "../hooks/useRefsInit.jsx";
import useEnableViewTracking from "../hooks/useEnableViewTracking.jsx";
import { VIEW_TYPES } from "../../other/constants/OtherKeys.jsx";
import { INCENTIVE_TYPES } from "@/other/constants/IncentiveKeys.jsx";
import { WIDGET_KEYS } from "../../other/constants/WidgetKeys.jsx";
import useViews from "@/react/hooks/useViews.jsx";
import { ENTITY_TYPES } from "@/other/constants/OtherKeys.jsx";
import ViewSpecificHeader from "../components/appHeader/ViewSpecificHeader.jsx";

const IncentivePointsPage = () => {
  const gridRef = useRef(null);
  const { setRefs, isRefsInit } = useRefsInit([gridRef]);
  const { user } = useContext(AuthContext);
  const { project, editorHandler, stateProvider, entityManager } = useContext(BryntumContext);
  useEnableViewTracking(gridRef, VIEW_TYPES.incentivePoints, isRefsInit);
  const viewManagerState = useViews();

  useEffect(() => {
    if (project) {
      swivuCheckGridGrants(gridRef.current.instance, user);
      callOnLoadOnColumns(gridRef.current.instance.columns, project, { editorHandler });
      gridRef.current.instance.store = project.incentivePointsStore;
    }
  }, [gridRef, project, user, editorHandler]);

  useEffect(() => {
    setTimeout(() => {
      gridRef.current.instance.store.group({ field: "incentiveStatus", ascending: false })
      const filterByIncentiveIds = history.state?.state?.filters?.incentiveIds;
      if (filterByIncentiveIds?.length > 0) {
        const incentiveRecords = project.outcomeIncentiveStore.query((record) => filterByIncentiveIds.includes(record.id), true);
        if (incentiveRecords.length) {
          // Todo: incentiveNameFilter and incentiveStatus key names should be shared here and in columns
          gridRef.current.instance.store.removeFilter("incentiveNameFilter");
          gridRef.current.instance.store.addFilter({
            id: "incentiveNameFilter",
            operator: "isIncludedIn",
            property: "incentiveName",
            value: incentiveRecords.map((record) => record.name)
          });
        }
      }
    }, 100);
  }, [gridRef, project, viewManagerState]);

  useEffect(() => {
    if (gridRef.current) {
      gridRef.current.instance.features.cellMenu.processItems = (e) => handleIncentiveGridContextMenuOptions(e, { editorHandler, user, project, grid: gridRef.current.instance });
    }
  }, [editorHandler, user, project]);

  const onCellDoubleClick = ({ record, column }) => {
    const originalRecord = getOriginalRecord(record);
    if (canOpenEditor(column, originalRecord)) {
      if (column.id === "task") {
        const { taskRecord } = record;
        if (taskRecord && taskRecord.isTask) {
          entityManager.openEntityManager({ entityType: ENTITY_TYPES.TASK, entityId: taskRecord.id });
        } else if (record.taskName) {
          showConfirmationDialog({
            title: "Task No Longer Available",
            message: `"${record.taskName}" has been deleted and is no longer accessible.`,
            isCancelButton: false
          });
        }
      } else if (column.id === "incentive") {
        const { incentive } = record;
        if (incentive) {
          if (record.incentiveTypeValue === INCENTIVE_TYPES.SPOT) {
            editorHandler.openEditor(EDITOR_KEYS.spotIncentiveEditor, incentive, { spotIncentive: incentive });
          } else {
            editorHandler.openEditor(EDITOR_KEYS.outcomeIncentiveEditor, incentive, { incentiveRecord: incentive, pointRecord: record });
          }
        }
      } else if (column.id === "eligible-board-user") {
        const { boardUser } = record;
        editorHandler.openEditor(EDITOR_KEYS.userEditor, boardUser, { user: boardUser });
      }
    }
  }

  const onGridCellClick = debounce(({ record, column, event }) => {
    if (canOpenEditor(column, record) && column.type === WIDGET_KEYS.incentivePointsStatusColumn) {
      editorHandler.openEditor(EDITOR_KEYS.entityTagEditor, null, {
        entityRecord: record,
        column,
        event
      }, { by: "recordType", useExisting: true })
    }
  }, 300)

  return (
    <>
      <ViewSpecificHeader />
      {user && (
        <AlertNotificationBanner project={project} editorHandler={editorHandler} stateProvider={stateProvider} user={user} />
      )}
      <BryntumGrid
        stateId
        ref={setRefs(gridRef)}
        {...INCENTIVE_POINTS_GRID_CONFIG}
        onCellDblClick={onCellDoubleClick}
        onCellClick={onGridCellClick}
        searchFeature={true}
      />
    </>
  );
};

export default IncentivePointsPage;
